import ListPageController, { type StatCardDef } from "./mixins/ListPageController";
import Fragment from "sap/ui/core/Fragment";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import JSONModel from "sap/ui/model/json/JSONModel";
import type { SearchField$LiveChangeEvent, SearchField$SearchEvent } from "sap/m/SearchField";
import type { Select$ChangeEvent } from "sap/m/Select";
import type Dialog from "sap/m/Dialog";
import type Input from "sap/m/Input";
import type TextArea from "sap/m/TextArea";
import type Select from "sap/m/Select";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

type StatusKey = "ALL" | "Active" | "Inactive" | "Deprecated";
type BillingKey = "ALL" | "Perpetual" | "PerpetualWithMaintenance" | "SubscriptionMonthly" | "SubscriptionAnnual";

interface ViewState {
	statusFilter: StatusKey;
	billingFilter: BillingKey;
	searchQuery: string;
}

interface VariantPreset {
	status: StatusKey;
	billing: BillingKey;
	search: string;
}

const VARIANTS: Record<string, VariantPreset> = {
	"standard":     { status: "ALL",        billing: "ALL",                search: "" },
	"active":       { status: "Active",     billing: "ALL",                search: "" },
	"subscription": { status: "Active",     billing: "SubscriptionAnnual", search: "" },
	"perpetual":    { status: "Active",     billing: "Perpetual",          search: "" },
	"deprecated":   { status: "Deprecated", billing: "ALL",                search: "" }
};

// Karty statystyk nad tabelą — ten sam fragment StatCardsRow co Organizations/Dashboard.
const STATS: readonly StatCardDef[] = [
	{ key: "total", labelKey: "products.stats.total.label", subtitleKey: "products.stats.total.subtitle", state: "Neutral" },
	{ key: "active", labelKey: "products.stats.active.label", subtitleKey: "products.stats.active.subtitle", state: "Good", filter: "Status eq 'Active'" },
	{ key: "inactive", labelKey: "products.stats.inactive.label", subtitleKey: "products.stats.inactive.subtitle", state: "Critical", filter: "Status eq 'Inactive'" },
	{ key: "deprecated", labelKey: "products.stats.deprecated.label", subtitleKey: "products.stats.deprecated.subtitle", state: "Error", filter: "Status eq 'Deprecated'" }
];

interface CreateProductRequest {
	productFamilyId: string;
	code: string;
	name: string;
	description: string | null;
	licensingMode: string;
	bundleKind: string;
	billingMode: string;
}

/**
 * @namespace computec.b2b.portal.controller
 */
export default class Products extends ListPageController {
	private addDialog?: Dialog;

	public override onInit(): void {
		const initial: ViewState = {
			statusFilter: "ALL",
			billingFilter: "ALL",
			searchQuery: ""
		};
		this.getView().setModel(new JSONModel(initial), "view");
		void this.initStatsCards("Products", STATS);
		this.getRouter().getRoute("products")?.attachPatternMatched(() => { void this.onShow(); });
	}

	private async onShow(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const title = bundle.getText("products.title") ?? "Products";
		this.setPageHeader({ title, subtitle: bundle.getText("products.subtitle") ?? "" });
		this.applyFilters();
		void this.refreshStatsCards();
	}

	public onSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("view") as JSONModel).setProperty("/searchQuery", raw);
		this.applyFilters();
	}

	public onStatusSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as StatusKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/statusFilter", key);
		this.applyFilters();
	}

	public onBillingSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as BillingKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/billingFilter", key);
		this.applyFilters();
	}

	public onVariantSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? "standard";
		const preset = VARIANTS[key];
		if (!preset) {
			return;
		}
		const model = this.getView().getModel("view") as JSONModel;
		model.setProperty("/statusFilter", preset.status);
		model.setProperty("/billingFilter", preset.billing);
		model.setProperty("/searchQuery", preset.search);
		this.applyFilters();
	}

	public onRefresh(): void {
		this.refreshTable("productTable");
		void this.refreshStatsCards();
	}

	public onOpenProduct(event: { getSource(): { getBindingContext(name: string): { getProperty(p: string): unknown } | null } }): void {
		const ctx = event.getSource().getBindingContext("odata");
		if (!ctx) {
			return;
		}
		const id = ctx.getProperty("Id") as string | undefined;
		if (id) {
			this.navTo("productDetail", { id });
		}
	}

	private applyFilters(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as ViewState;
		const parts: string[] = [];

		// Raw $filter — UI5 ODataListBinding nie umie format'ować literałów dla custom .NET enum types.
		// tolower(Name)/(Code) — case-insensitive search po nazwie i kodzie produktu.
		if (state.searchQuery.trim().length > 0) {
			const qLower = escapeOData(state.searchQuery.trim().toLowerCase());
			parts.push(`(contains(tolower(Name),'${qLower}') or contains(tolower(Code),'${qLower}'))`);
		}
		if (state.statusFilter !== "ALL") {
			parts.push(`Status eq '${state.statusFilter}'`);
		}
		if (state.billingFilter !== "ALL") {
			parts.push(`BillingMode eq '${state.billingFilter}'`);
		}

		const binding = this.getTableBinding("productTable");
		binding?.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}

	public async onAddProductPress(): Promise<void> {
		if (!this.addDialog) {
			this.addDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.AddProductDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.addDialog);
		}
		this.resetAddForm();
		this.addDialog.open();
	}

	public onCancelProduct(): void {
		this.addDialog?.close();
	}

	public async onSaveProduct(): Promise<void> {
		const dialog = this.addDialog;
		if (!dialog) {
			return;
		}

		const familyId = (this.byFragmentId("ddProductFamily") as Select).getSelectedKey();
		const code = (this.byFragmentId("inpCode") as Input).getValue().trim();
		const name = (this.byFragmentId("inpName") as Input).getValue().trim();
		const description = (this.byFragmentId("inpDescription") as TextArea).getValue().trim();
		const licensingMode = (this.byFragmentId("ddLicensingMode") as Select).getSelectedKey();
		const bundleKind = (this.byFragmentId("ddBundleKind") as Select).getSelectedKey();
		const billingMode = (this.byFragmentId("ddBillingMode") as Select).getSelectedKey();

		const bundle = await this.getResourceBundle();
		const missing: string[] = [];
		if (!familyId) missing.push("productFamily");
		if (!code) missing.push("code");
		if (!name) missing.push("name");
		if (!licensingMode) missing.push("licensingMode");
		if (!bundleKind) missing.push("bundleKind");
		if (!billingMode) missing.push("billingMode");
		if (missing.length > 0) {
			MessageBox.warning(bundle.getText("products.form.required") ?? "Required field missing");
			return;
		}

		const payload: CreateProductRequest = {
			productFamilyId: familyId,
			code,
			name,
			description: description ? description : null,
			licensingMode,
			bundleKind,
			billingMode
		};

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json("/api/v1/products", {
				method: "POST",
				body: JSON.stringify(payload)
			});
			MessageToast.show(bundle.getText("products.add.success") ?? "Product created");
			dialog.close();
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("products.add.failure") ?? "Failed to create product"));
		}
	}

	private byFragmentId(id: string): unknown {
		return this.byId(id);
	}

	private resetAddForm(): void {
		(this.byFragmentId("inpCode") as Input).setValue("");
		(this.byFragmentId("inpName") as Input).setValue("");
		(this.byFragmentId("inpDescription") as TextArea).setValue("");
		(this.byFragmentId("ddProductFamily") as Select).setSelectedKey("");
		(this.byFragmentId("ddLicensingMode") as Select).setSelectedKey("");
		(this.byFragmentId("ddBundleKind") as Select).setSelectedKey("");
		(this.byFragmentId("ddBillingMode") as Select).setSelectedKey("");
	}

}

function escapeOData(value: string): string {
	return value.replace(/'/g, "''");
}
